import { createReducer } from 'typesafe-actions';
import { PDPProductFormatter } from '@westwing/appshell-formatting-helpers';
import { defaultErrorHandler, defaultRequestHandler } from '../dataFetching/generators';
import generateProductPlaceHolders from '../products/productPlaceholder';
import { PriceFormatConfigOptions } from '../products/types';
import { LookProductResponse } from '../lookDetail/types';
import { HomePageState, HomepageItems, PermanentBanner, PromotedLook, PromotedLookRaw } from './types';
import { homepageActions, HomePageActions } from './action';

const mapPromotedLookData = (data: PromotedLookRaw, priceFormat?: PriceFormatConfigOptions): PromotedLook => {
    const { products } = data;

    return {
        altTag: data.moodImage?.description,
        id: data.id,
        name: data.name,
        trackingName: data.trackingName || '',
        caption: data.caption,
        slug: data.slug,
        color: data.color,
        isLandscape: data.isLandscape,
        products: products.map((value: LookProductResponse, index: number) => ({
            ...PDPProductFormatter(index, value, undefined, undefined, priceFormat),
            ...(value.meta?.hotspot ? { hotspot: value.meta?.hotspot } : {}),
        })),
        moodImage: {
            url: data.moodImage?.file?.url || '',
            seoUrl: data.moodImage?.seo_url || '',
        },
    };
};

const defaultBanner = {
    mobileImage: '',
    desktopImage: '',
    targetUrl: '',
    trackingName: '',
    altText: '',
};

const defaultPromotedLook: PromotedLook = {
    id: '',
    name: '',
    trackingName: '',
    caption: '',
    slug: '',
    color: '',
    isLandscape: false,
    products: generateProductPlaceHolders(4),
    altTag: '',
    moodImage: { url: '', seoUrl: '' },
};

export const defaultHomeItemsState: HomepageItems = {
    categoriesSlider: [],
    heroBanner: { altText: '', desktopImage: '', mobileImage: '', targetUrl: '', trackingName: '' },
    placingBanners: [],
    portraitBanners: [],
    permanentBanners: [],
    ugcGalleryBanner: defaultBanner,
    referAFriendBanner: defaultBanner,
    secondaryBanner: {
        ctaText: '',
        desktopImage: '',
        trackingName: '',
        targetUrl: '',
        mobileImage: '',
        subtitle: '',
        title: '',
    },
    promotedLook: defaultPromotedLook,
};

export const defaultHomePageState = {
    isFetching: false,
    didInvalidate: false,
    error: null,
    identifier: '',
    homepageItems: defaultHomeItemsState,
};

export const validPermanentBanners = (banners: Array<PermanentBanner>): Array<PermanentBanner> =>
    banners.filter(banner => banner.targetUrlDesktop && banner.targetUrlMobile && banner.image && banner.trackingName);

const homepageReducer = createReducer<HomePageState, HomePageActions>(defaultHomePageState)
    .handleAction(homepageActions.actions.success, (state, action) => {
        const { reducerPayloadOptions } = action.payload;
        const formatOptions = { countryCode: '', currency: '' };
        if (reducerPayloadOptions) {
            formatOptions.countryCode = reducerPayloadOptions.countryCode;
            formatOptions.currency = reducerPayloadOptions.currency;
        }

        const serverItems = action.payload.data.data.home_items;
        const homepageItems = {
            ...serverItems,
            permanentBanners: validPermanentBanners(serverItems.permanentBanners),
            promotedLook: mapPromotedLookData(serverItems.promotedLook, formatOptions),
        };
        return {
            ...state,
            homepageItems,
        };
    })
    .handleAction(homepageActions.actions.failure, (state, action) => defaultErrorHandler(state, action))
    .handleAction(homepageActions.actions.request, (state, action) => defaultRequestHandler(state, action));

export default homepageReducer;
